import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
    FiHome,
    FiShoppingBag,
    FiShoppingCart,
    FiUser,
    FiChevronLeft,
    FiChevronRight,
} from "react-icons/fi";
import Header from "./Header";
import ipx_logo from "../images/logo-the-collection-grid.png";
import ipx_logo_collapsed from "../images/logo-tcgZone.png";
import SidebarIconHome from "../images/icon/pkicon-home.png";
import SidebarIconGacha from "../images/icon/pkicon-gacha.png";
import SidebarIconGachaMenu from "../images/icon/pkicon-sidebar-gacha.svg";
import SidebarIconMarketplace from "../images/icon/pkicon-marketplace.png";
import SidebarIconBuypack from "../images/icon/pkicon-vault.png";
import SidebarIconProfile from "../images/icon/pkicon-profile.png";
import mainVideoBg from "../video/galaxy-video.mp4";

import { useTranslation } from "../hooks/useTranslation";
import { useWallet } from "../hooks/useWallet";
import { FiAlertCircle, FiX } from "react-icons/fi";
import MobileNavbar from "./MobileNavbar";

function useMediaQuery(query) {
    const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

    useEffect(() => {
        const mq = window.matchMedia(query);
        const handler = (e) => setMatches(e.matches);
        mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
        return () => {
            mq.removeEventListener ? mq.removeEventListener("change", handler) : mq.removeListener(handler);
        };
    }, [query]);

    return matches;
}

export default function Layout({ children }) {
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
    const location = useLocation();
    const { t } = useTranslation();
    const { showNetworkSwitchModal, setShowNetworkSwitchModal, switchNetwork, isSwitchingNetwork } = useWallet();
    const toggleSidebar = () => {
        if (isMobile) {
            // ✅ mobile：关掉抽屉
            setIsMobileSidebarOpen(false);
            setIsSidebarCollapsed(true); // 可选：恢复 icon-only 状态
        } else {
            // ✅ desktop：正常折叠/展开
            setIsSidebarCollapsed((v) => !v);
        }
    };


    const navigationItems = [
        { path: "/main", label: t("header.home"), iconType: "image", icon: SidebarIconHome },
        // Temporarily hidden: Gacha sidebar entry. Keep SidebarIconGachaMenu import for quick restore.
        // { path: "/gacha", label: t("header.gacha"), iconType: "image", icon: SidebarIconGachaMenu },
        { path: "/gacha", label: t("header.gacha"), iconType: "image", icon: SidebarIconGacha },
        // { path: "/vault", label: t("library.title"), iconType: "image", icon: SidebarIconBuypack },
        { path: "/marketplace", label: t("header.marketplace"), iconType: "image", icon: SidebarIconMarketplace },
        // { path: "/buy-pack", label: t("header.buyPack"), iconType: "image", icon: SidebarIconBuypack },
        { path: "/profile", label: t("header.profile"), iconType: "image", icon: SidebarIconProfile },
    ];

    // Sidebar toggle js 
    const isMobile = useMediaQuery("(max-width: 768px)");
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    useEffect(() => {
        if (isMobile) {
            setIsSidebarCollapsed(true);
            setIsMobileSidebarOpen(false);
        }
    }, [isMobile]);
    const showLabel = !isSidebarCollapsed || (isMobile && isMobileSidebarOpen);
    const isStandaloneFlowPage = location.pathname === "/unlimited-pack";
    const isMobileStandaloneHistory = isMobile && (location.pathname === "/limited-pack-history" || location.pathname === "/pack-history");
    const isCheckoutPage = isStandaloneFlowPage || isMobileStandaloneHistory;

    return (
        <div
            style={{
                display: "flex",
                height: "100vh",
                overflow: "hidden",
                backgroundColor: "#000",
            }}
        >
            {/* Sidebar */}
            {!isCheckoutPage && <aside
                style={{
                    width: isSidebarCollapsed ? "80px" : "240px",
                    backgroundColor: "#1a1a1a",
                    borderRight: "1px solid rgba(255,255,255,0.12)",
                    display: "flex",
                    flexDirection: "column",
                    transition: "transform 0.25s ease, width 0.3s ease",
                    zIndex: 50,

                    // ✅ Desktop：sticky
                    position: isMobile ? "fixed" : "sticky",
                    top: 0,
                    left: 0,
                    height: "100vh",
                    flexShrink: 0,

                    // ✅ Mobile：抽屉滑入滑出
                    transform: isMobile
                        ? (isMobileSidebarOpen ? "translateX(0)" : "translateX(-100%)")
                        : "translateX(0)",

                    // ✅ Mobile：抽屉宽度建议用 240（别 80）
                    width: isMobile ? "240px" : (isSidebarCollapsed ? "100px" : "240px"),
                }}
            >
                {/* Logo Section */}
                <div
                    style={{
                        height: "80px",
                        padding: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: showLabel ? "center" : "center",
                        borderBottom: "1px solid rgba(255,255,255,0.12)",
                    }}
                >
                    {!showLabel ? (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                                transition: "transform, opacity, background-color, border-color, color 0.3s ease",
                            }}
                        >
                            <img
                                src={ipx_logo_collapsed}
                                alt="TCG Zon Logo"
                                style={{
                                    width: "60px",
                                    maxWidth: "60px",
                                    height: "auto",
                                    display: "block",
                                }}
                            />
                        </div>
                    ) : (
                        <div style={{ maxWidth: "150px", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <img
                                src={ipx_logo}
                                alt="TCG Zon Logo"
                                style={{
                                    width: "100%",
                                    height: "auto",
                                    display: "block",
                                    transition: "transform, opacity, background-color, border-color, color 0.3s ease",
                                }}
                            />
                        </div>
                    )}
                </div>

                {/* Navigation Items */}
                <nav
                    style={{
                        flex: 1,
                        padding: "16px 0",
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                        overflow: "hidden",
                    }}
                >
                    {navigationItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = location.pathname.includes(item.path);

                        return (
                            <Link
                                key={item.path}
                                to={item.path}
                                className={`sidebar-item ${isActive ? "active" : ""}`.trim()}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: isSidebarCollapsed ? "center" : "flex-start",
                                    gap: "12px",
                                    padding: "12px 16px",
                                    margin: "0 12px",
                                    borderRadius: "14px",
                                    textDecoration: "none",
                                    color: isActive ? "#fff" : "rgba(255,255,255,0.6)",
                                    background: isActive
                                        ? "linear-gradient(135deg, rgba(133, 114, 195, 0.2) 0%, rgba(31, 19, 70, 0.5) 100%)"
                                        : "transparent",
                                    border: isActive
                                        ? "1px solid rgba(133, 114, 195, 0.4)"
                                        : "1px solid transparent",
                                    borderLeft: isActive
                                        ? "3px solid rgba(187, 166, 255, 1)"
                                        : "1px solid transparent",
                                    boxShadow: isActive
                                        ? "0 8px 20px rgba(0, 0, 0, 0.3), inset 0 0 12px rgba(133, 114, 195, 0.2)"
                                        : "none",
                                    backdropFilter: isActive ? "blur(10px)" : "none",
                                    transition: "transform, opacity, background-color, border-color, color 0.3s ease",
                                    cursor: "pointer",
                                }}
                                onMouseEnter={(e) => {
                                    if (!isActive) {
                                        e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                                        e.currentTarget.style.color = "#fff";
                                        const img = e.currentTarget.querySelector("img");
                                        if (img) img.style.opacity = "1";
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isActive) {
                                        e.currentTarget.style.background = "transparent";
                                        e.currentTarget.style.color = "rgba(255,255,255,0.6)";
                                        const img = e.currentTarget.querySelector("img");
                                        if (img) img.style.opacity = "0.7";
                                    }
                                }}
                                onClick={() => {
                                    if (isMobile) {
                                        setIsMobileSidebarOpen(false);
                                        setIsSidebarCollapsed(true);
                                    }
                                }}
                            >
                                {item.iconType === "image" && (
                                    <img
                                        src={item.icon}
                                        alt=""
                                        style={{
                                            width: "30px",
                                            opacity: isActive ? 1 : 0.7,
                                            filter: isActive ? "drop-shadow(0 0 8px rgba(133, 114, 195, 0.6))" : "none",
                                            transition: "transform, opacity, background-color, border-color, color 0.3s ease",
                                        }}
                                    />
                                )}
                                {showLabel && (
                                    <span
                                        style={{
                                            fontSize: "15px",
                                            fontWeight: isActive ? 600 : 500,
                                            letterSpacing: "0.3px",
                                            textShadow: isActive ? "0 0 10px rgba(133, 114, 195, 0.5)" : "none",
                                        }}
                                    >
                                        {item.label}
                                    </span>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* Collapse Toggle Button */}
                <div
                    style={{
                        padding: "16px",
                        borderTop: "1px solid rgba(255,255,255,0.12)",
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    <button
                        onClick={toggleSidebar}
                        style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: isSidebarCollapsed ? "center" : "flex-end",
                            padding: "8px",
                            backgroundColor: "transparent",
                            border: "none",
                            color: "rgba(255,255,255,0.7)",
                            cursor: "pointer",
                            borderRadius: "6px",
                            transition: "background-color 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor =
                                "rgba(255,255,255,0.08)";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                        }}
                    >
                        {isSidebarCollapsed ? (
                            <FiChevronRight style={{ fontSize: "20px" }} />
                        ) : (
                            <FiChevronLeft style={{ fontSize: "20px" }} />
                        )}
                    </button>
                </div>
            </aside>}

            {!isCheckoutPage && isMobile && isMobileSidebarOpen && (
                <div
                    onClick={() => { setIsMobileSidebarOpen(false); setIsSidebarCollapsed(true); }}
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.55)",
                        zIndex: 40,
                    }}
                />
            )}

            {/* Main Content Area */}
            <div
                style={{
                    flex: 1,
                    height: isCheckoutPage ? "100dvh" : "100vh",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    /*backgroundColor: isCheckoutPage ? "#020207" : "#0a0a0a",*/
                    minWidth: 0,
                }}
            >
                {/* Header */}
                {!isCheckoutPage && <div
                    style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 30,
                        flexShrink: 0,
                        borderBottom: "1px solid rgba(255,255,255,0.12)",
                        background: "#0a0a0a",
                    }}
                >
                    <Header
                        isMobile={isMobile}
                        onOpenSidebar={() => {
                            setIsMobileSidebarOpen(true)
                            setIsSidebarCollapsed(false);
                        }
                        }
                    />
                </div>}

                {/* Page Content */}
                <main
                    className={
                        location.pathname === "/home" ? "buypack-wrapper" :
                            location.pathname === "/main" ? "main-wrapper" :
                                location.pathname.includes("vault") ? "vault-wrapper" :
                                    location.pathname.startsWith("/library") ? "library-wrapper" : ""
                    }
                    style={{
                        flex: 1,
                        padding: isCheckoutPage ? 0 : (isMobile ? "24px 24px 100px 24px" : "24px"),
                        overflowY: isCheckoutPage ? "hidden" : "auto",
                        overflowX: "hidden",
                    }}
                >
                    {location.pathname === "/main" && (
                        <video
                            className="bg-video-element"
                            autoPlay
                            loop
                            muted
                            playsInline
                        >
                            <source src="https://s3.ap-southeast-1.amazonaws.com/files.pokemon.box/galaxy-video.mp4" type="video/mp4" />
                        </video>
                    )}
                    <div
                        style={{
                            position: "relative",
                            zIndex: 1,
                            minHeight: "100%",
                            ...(location.pathname === "/main" ? { display: "flex", alignItems: "center" } : {})
                        }}
                    >
                        {children}
                    </div>
                </main>
            </div>

            {/* Network Switch Modal - Only show when wallet is connected but on wrong network */}
            {showNetworkSwitchModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(0,0,0,0.8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 48,
                        padding: 20,
                    }}
                    onClick={() => {
                        // Don't allow closing by clicking outside - user must switch network
                    }}
                >
                    <div
                        style={{
                            background: "linear-gradient(135deg, rgba(18,18,18,0.95) 0%, rgba(31,19,70,0.95) 100%)",
                            borderRadius: 24,
                            maxWidth: 480,
                            width: "100%",
                            padding: 40,
                            border: "1px solid rgba(151, 112, 221, 0.3)",
                            boxShadow: "0 24px 60px rgba(0,0,0,0.8)",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Icon */}
                        <div
                            style={{
                                width: 64,
                                height: 64,
                                borderRadius: "50%",
                                background: "rgba(255, 193, 7, 0.15)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto 24px",
                            }}
                        >
                            <FiAlertCircle
                                style={{
                                    fontSize: 32,
                                    color: "#FFC107",
                                }}
                            />
                        </div>

                        {/* Title */}
                        <div
                            style={{
                                fontSize: 24,
                                fontWeight: 600,
                                color: "#fff",
                                textAlign: "center",
                                marginBottom: 16,
                            }}
                        >
                            {t("layout.pleaseSwitchToBSC")}
                        </div>

                        {/* Message */}
                        <div
                            style={{
                                fontSize: 14,
                                color: "rgba(255,255,255,0.7)",
                                textAlign: "center",
                                marginBottom: 32,
                                lineHeight: 1.6,
                            }}
                        >
                            {t("layout.switchMessage")}
                        </div>

                        {/* Switch Button */}
                        <button
                            onClick={async () => {
                                const success = await switchNetwork();
                                if (success) {
                                    setShowNetworkSwitchModal(false);
                                }
                            }}
                            disabled={isSwitchingNetwork}
                            style={{
                                width: "100%",
                                padding: "16px 24px",
                                borderRadius: 12,
                                background: isSwitchingNetwork
                                    ? "rgba(151, 112, 221, 0.5)"
                                    : "linear-gradient(135deg, rgba(151, 112, 221, 0.8) 0%, rgba(139, 92, 246, 0.8) 100%)",
                                border: "none",
                                color: "#fff",
                                fontSize: 16,
                                fontWeight: 600,
                                cursor: isSwitchingNetwork ? "not-allowed" : "pointer",
                                transition: "transform, opacity, background-color, border-color, color 0.2s ease",
                                opacity: isSwitchingNetwork ? 0.6 : 1,
                            }}
                            onMouseEnter={(e) => {
                                if (!isSwitchingNetwork) {
                                    e.currentTarget.style.background = "linear-gradient(135deg, rgba(151, 112, 221, 1) 0%, rgba(139, 92, 246, 1) 100%)";
                                    e.currentTarget.style.transform = "translateY(-2px)";
                                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(151, 112, 221, 0.3)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isSwitchingNetwork) {
                                    e.currentTarget.style.background = "linear-gradient(135deg, rgba(151, 112, 221, 0.8) 0%, rgba(139, 92, 246, 0.8) 100%)";
                                    e.currentTarget.style.transform = "translateY(0)";
                                    e.currentTarget.style.boxShadow = "none";
                                }
                            }}
                        >
                            {isSwitchingNetwork ? t("layout.switchingNetwork") : t("layout.switchToBNBNetwork")}
                        </button>
                    </div>
                </div>
            )}
            {!isCheckoutPage && isMobile && <MobileNavbar />}
        </div>

    );
}

